import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import type {
  AssistantAnswer,
  AssistantErrorCode,
  AssistantTranscriptEntry,
  AssistantTurn,
} from "./types";

/**
 * The widget's whole state, as one reducer rather than a handful of flags
 * (ticket 21, §D1, §UI).
 *
 * The clearing rules are the security-relevant part and they are all here, in
 * one place, so none of them can be forgotten at a call site:
 *
 *  - a **user or entity change** clears conversation *and* draft and aborts the
 *    request in flight;
 *  - a **`409 context_changed`** does the same and says why;
 *  - a **session end** does the same and asks `AuthContext` to re-read
 *    `/auth/me` — the assistant's `401 session_changed` is also what an entity
 *    switch in another tab produces, and the user may be perfectly signed in,
 *    which is why `/api/assistant` is excluded from the global 401 redirect;
 *  - a **page reload** clears by construction: nothing is persisted anywhere;
 *  - an ordinary **cookie refresh clears nothing**, because the binding is an
 *    HMAC over person + entity and not the sid.
 *
 * A late response whose `authContextId` no longer matches the one we hold is
 * discarded here as well as refused by the server — belt and braces, because the
 * abort and the response can race.
 */

// The cap itself lives in `./limits` so `AssistantComposer` can read it
// without importing this hook — and with it `AuthContext` and axios — into the
// public landing bundle (ticket 21.5). Re-exported: no call site changed.
export { QUESTION_MAX_CHARS } from "./limits";
const TRANSCRIPT_TURNS = 6;
const TRANSCRIPT_CHARS_PER_TURN = 2_000;
const TRANSCRIPT_CHARS_TOTAL = 8_000;

const API_URL = process.env.REACT_APP_API_URL || "";

export type AssistantNotice = "contextChanged" | "sessionEnded" | "cleared" | null;

interface State {
  turns: AssistantTurn[];
  draft: string;
  pending: boolean;
  /**
   * Set while `/auth/me` is being re-read after a context change, and cleared
   * only when it has answered (code review r2, finding 5).
   *
   * Without it the 409 handler reset `pending`, started `refreshAuth()` without
   * awaiting it, and let the next question go — **unbound**, so the server
   * answered it for the new organisation while the panel still named the old
   * one. The whole point of the 409 is that the user must not be shown one
   * organisation's answer under another's name.
   */
  refreshing: boolean;
  notice: AssistantNotice;
  /** The context the conversation belongs to; sent back with every question. */
  authContextId: string | null;
  seq: number;
  /**
   * Bumped by every reset. A response that belongs to an older generation is
   * discarded, whatever it says: the abort already covers the ordinary case, but
   * an abort and a resolved response can race, and a stale answer landing in a
   * cleared panel is exactly the mixing §D1 forbids.
   */
  generation: number;
}

type Action =
  | { type: "draft"; value: string }
  | { type: "ask"; text: string }
  | { type: "answered"; answer: AssistantAnswer }
  | { type: "failed"; code: AssistantErrorCode }
  | { type: "stopped" }
  | { type: "reset"; notice: AssistantNotice; refreshing?: boolean }
  | { type: "refreshed" };

const initial: State = {
  turns: [],
  draft: "",
  pending: false,
  refreshing: false,
  notice: null,
  authContextId: null,
  seq: 0,
  generation: 0,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "draft":
      return { ...state, draft: action.value };
    case "ask":
      return {
        ...state,
        // The draft is consumed by the question, not kept — re-sending the same
        // text by accident is worse than retyping it.
        draft: "",
        pending: true,
        notice: null,
        seq: state.seq + 1,
        turns: [
          ...state.turns,
          { kind: "question", id: `q${state.seq + 1}`, text: action.text },
        ],
      };
    case "answered":
      return {
        ...state,
        pending: false,
        authContextId: action.answer.authContextId,
        turns: [
          ...state.turns,
          {
            kind: "answer",
            id: `a${state.seq}`,
            receivedAt: Date.now(),
            answer: action.answer,
          },
        ],
      };
    case "failed":
      return {
        ...state,
        pending: false,
        turns: [...state.turns, { kind: "error", id: `e${state.seq}`, code: action.code }],
      };
    case "stopped":
      // Only a request in flight can be stopped. A stale Stop — say, one that
      // lands after a 409/401 reset, before the next render has told `stop`
      // that nothing is in flight — must not leave a lone «Stopped» in an
      // emptied chat (code review 21.4, F3).
      if (!state.pending) return state;
      return {
        ...state,
        pending: false,
        turns: [...state.turns, { kind: "stopped", id: `s${state.seq}` }],
      };
    case "reset":
      return {
        ...initial,
        // A reset with nothing to announce keeps the notice already standing.
        // An identity change can arrive in two steps — the old entity goes
        // away before the new one is known — and the second step has an empty
        // conversation by then, so passing `null` through wiped the
        // explanation the first step had just put on screen. The user watched
        // their chat empty itself with no reason given.
        notice: action.notice ?? state.notice,
        refreshing: action.refreshing === true,
        generation: state.generation + 1,
      };
    case "refreshed":
      return { ...state, refreshing: false };
    default:
      return state;
  }
}

/** The last six turns, capped per turn and in total, exactly as the server's
 *  schema will re-check them. Trimming here is a courtesy; the refusal there is
 *  the rule.
 *
 *  A question the person stopped is left out (ticket 21.4): sending it back
 *  would let the next answer quietly answer the abandoned question too. */
export function buildTranscript(turns: AssistantTurn[]): AssistantTranscriptEntry[] {
  const entries: AssistantTranscriptEntry[] = [];
  for (let index = 0; index < turns.length; index += 1) {
    const turn = turns[index];
    if (turn.kind === "question") {
      if (turns[index + 1]?.kind === "stopped") continue;
      entries.push({ role: "user", text: turn.text.slice(0, TRANSCRIPT_CHARS_PER_TURN) });
    } else if (turn.kind === "answer" && turn.answer.answer.trim()) {
      entries.push({
        role: "assistant",
        text: turn.answer.answer.slice(0, TRANSCRIPT_CHARS_PER_TURN),
      });
    }
  }

  const recent = entries.slice(-TRANSCRIPT_TURNS);
  // Drop from the *front* until the total fits: the newest turns are the ones
  // the follow-up actually refers to.
  let total = recent.reduce((sum, entry) => sum + entry.text.length, 0);
  while (recent.length > 0 && total > TRANSCRIPT_CHARS_TOTAL) {
    total -= recent[0].text.length;
    recent.shift();
  }
  return recent;
}

function codeFor(status: number, code: string | undefined): AssistantErrorCode {
  if (status === 403) return "access";
  if (status === 429) return code === "budget_exhausted" ? "limit" : "busy";
  if (status === 400) return "tooLong";
  if (status === 503 && code === "timeout") return "timeout";
  return "unavailable";
}

export interface AssistantPageContext {
  route: string;
  objectType?: "order" | "contract";
  objectId?: string;
}

export interface UseAssistant {
  turns: AssistantTurn[];
  draft: string;
  /** True while a request is in flight **or** while the identity is being
   *  re-read: in both cases a new question must wait. */
  pending: boolean;
  /** A request is in flight — the only wait the person may cut short (21.4). */
  requesting: boolean;
  /** The identity is being re-read after a 409/401. Not stoppable. */
  refreshing: boolean;
  notice: AssistantNotice;
  setDraft: (value: string) => void;
  send: (text: string) => Promise<void>;
  /** Abandon the request in flight. Its answer, if one still arrives, is
   *  dropped, and its question is not sent back as context. */
  stop: () => void;
  clear: () => void;
}

export function useAssistant(pageContext: AssistantPageContext): UseAssistant {
  const [state, dispatch] = useReducer(reducer, initial);
  const { i18n } = useTranslation();
  const { entityId, entityType, user, refreshAuth, isLoading } = useAuth();
  const inFlight = useRef<AbortController | null>(null);
  // Read inside the request without making `send` depend on it — the value that
  // matters is the one held when the response arrives.
  const contextRef = useRef<string | null>(null);
  contextRef.current = state.authContextId;
  const generationRef = useRef(0);
  generationRef.current = state.generation;
  /** Read by `stop`, so a stale click can never add «Stopped» to a turn that
   *  already ended, or to a conversation the identity reset just emptied. */
  const requestingRef = useRef(false);
  requestingRef.current = state.pending;

  const identity = `${user?.id ?? ""}|${entityType ?? ""}|${entityId ?? ""}`;
  /**
   * The identity this conversation belongs to, recorded only once `AuthContext`
   * has actually settled. Recording it on the first render instead would make a
   * plain page reload announce «организация изменилась»: the first render happens
   * while `/auth/me` is still in flight, so the entity arrives *after* the
   * baseline was taken and the effect reads a genuine login as a switch.
   */
  const baseline = useRef<string | null>(null);
  /** Read inside the identity effect, which must not depend on the state it
   *  inspects — `turns` grows between renders and a stale closure would miss the
   *  turn that was just added. */
  const hasContent = useRef(false);
  hasContent.current = state.turns.length > 0 || state.draft.trim().length > 0;

  // A user or entity change clears everything and aborts what is in flight.
  useEffect(() => {
    if (isLoading) return;
    if (baseline.current === null) {
      baseline.current = identity;
      return;
    }
    if (identity === baseline.current) return;
    const had = hasContent.current;
    baseline.current = identity;
    inFlight.current?.abort();
    inFlight.current = null;
    // Say "the chat was cleared" only when there was something to clear.
    // Signing in as an entity is itself an identity change (admin → building),
    // so without this the very first thing a user sees on opening the assistant
    // is a warning about a conversation they never had. A `null` here no longer
    // erases a notice already standing — see the reducer.
    dispatch({ type: "reset", notice: had ? "contextChanged" : null });
  }, [identity, isLoading]);

  // Nothing outlives the widget: an open request is abandoned on unmount.
  useEffect(() => () => inFlight.current?.abort(), []);

  const setDraft = useCallback((value: string) => {
    dispatch({ type: "draft", value });
  }, []);

  const clear = useCallback(() => {
    inFlight.current?.abort();
    inFlight.current = null;
    dispatch({ type: "reset", notice: "cleared" });
  }, []);

  /**
   * Stop (ticket 21.4). Aborting closes the connection, and the route aborts
   * the turn when it does — but the model attempt already under way keeps its
   * budget reservation, so stopping gives control back without saving money.
   * The late response is dropped by the `aborted` checks in `send`.
   */
  const stop = useCallback(() => {
    const controller = inFlight.current;
    if (!controller || !requestingRef.current) return;
    inFlight.current = null;
    controller.abort();
    dispatch({ type: "stopped" });
  }, []);

  const send = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || state.pending || state.refreshing) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        dispatch({ type: "failed", code: "offline" });
        return;
      }

      const transcript = buildTranscript(state.turns);
      const sentContext = contextRef.current;
      const sentGeneration = generationRef.current;
      dispatch({ type: "ask", text });

      const controller = new AbortController();
      inFlight.current = controller;

      try {
        const response = await fetch(`${API_URL}/api/assistant/messages`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            // The language the person **chose** in the interface, not the one
            // their browser happens to advertise (ticket 21.1 smoke). The
            // server renders product help and stored notifications itself, so
            // a Russian-speaking user on an `en-US` Chrome was handed an
            // English topic under a Russian answer.
            "Accept-Language": i18n.language || "et",
          },
          signal: controller.signal,
          body: JSON.stringify({
            question: text,
            transcript,
            ...(sentContext ? { authContextId: sentContext } : {}),
            ...(pageContext ? { pageContext } : {}),
          }),
        });

        if (controller.signal.aborted || sentGeneration !== generationRef.current) return;

        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as { code?: string };
          // The `.catch` above swallows an abort, so a request stopped while its
          // error body was being read must be re-checked here — otherwise it
          // renders an error under «Stopped» (spec review 21.4, F2).
          if (controller.signal.aborted || sentGeneration !== generationRef.current) return;
          if (response.status === 409) {
            // The request is over; nothing is left for Stop to abort (21.4, F3).
            inFlight.current = null;
            // Re-read `/auth/me` as well (code review 21, finding 3). Clearing
            // the binding alone left the panel showing organisation A — its
            // header, its example questions and its page context — while the
            // next, unbound request was answered for organisation B. The server
            // was right both times; the widget was describing the wrong one.
            // Sending stays closed until the refresh has answered, so the next
            // question cannot be asked under an identity the panel is still
            // showing incorrectly.
            dispatch({ type: "reset", notice: "contextChanged", refreshing: true });
            try {
              await refreshAuth();
            } finally {
              dispatch({ type: "refreshed" });
            }
            return;
          }
          if (response.status === 401) {
            inFlight.current = null;
            // Also what an entity switch in another tab produces — the widget
            // clears itself and lets AuthContext decide whether the session is
            // really gone.
            dispatch({ type: "reset", notice: "sessionEnded", refreshing: true });
            try {
              await refreshAuth();
            } finally {
              dispatch({ type: "refreshed" });
            }
            return;
          }
          dispatch({ type: "failed", code: codeFor(response.status, body.code) });
          return;
        }

        const body = (await response.json()) as { data: AssistantAnswer };
        // A late response from a context we no longer hold is dropped, even
        // though the server would have refused a *new* request under it. So is
        // one whose body was already buffered when the person pressed Stop.
        if (controller.signal.aborted || sentGeneration !== generationRef.current) return;
        if (sentContext && contextRef.current && sentContext !== contextRef.current) return;
        dispatch({ type: "answered", answer: body.data });
      } catch (error) {
        if (controller.signal.aborted || sentGeneration !== generationRef.current) return;
        dispatch({ type: "failed", code: "unavailable" });
      } finally {
        if (inFlight.current === controller) inFlight.current = null;
      }
    },
    [i18n.language, pageContext, refreshAuth, state.pending, state.refreshing, state.turns]
  );

  return useMemo(
    () => ({
      turns: state.turns,
      draft: state.draft,
      pending: state.pending || state.refreshing,
      requesting: state.pending,
      refreshing: state.refreshing,
      notice: state.notice,
      setDraft,
      send,
      stop,
      clear,
    }),
    [state.turns, state.draft, state.pending, state.refreshing, state.notice, setDraft, send, stop, clear]
  );
}
